import type { Dispatch } from "react"

import type { SegmentAnalysisAction, SegmentAnalysisState } from "../hooks/use-segment-analysis"
import type {
  Segment,
  SegmentAnalysisDataSource,
  SegmentAnalysisLoadStatus,
  SegmentAnalysisRole,
} from "../types"

import { AlertTriangle, BriefcaseBusiness, UserRound } from "@/components/icons"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"

import { APP_NAME } from "../utils/branding"
import { SEGMENT_DEMO_DATA } from "../utils/demo-mode"

type SegmentAnalysisHeaderProps = {
  allSegments: Segment[]
  backendError: string | null
  backendGeneratedAt: string | null
  backendStatus: SegmentAnalysisLoadStatus
  dataSource: SegmentAnalysisDataSource
  dispatch: Dispatch<SegmentAnalysisAction>
  effectiveRole: SegmentAnalysisRole
  owners: string[]
  state: SegmentAnalysisState
}

const ROLE_LABELS: Record<SegmentAnalysisRole, string> = {
  admin: "Sales Admin",
  owner: "Segment Owner",
  viewer: "Sales Viewer",
}

export function SegmentAnalysisHeader({
  allSegments,
  backendError,
  backendGeneratedAt,
  backendStatus,
  dataSource,
  dispatch,
  effectiveRole,
  owners,
  state,
}: SegmentAnalysisHeaderProps) {
  // The role toggle + persona picker are demo affordances; against the live
  // backend the role is resolved from the user's identity (§5.7) and shown read-only.
  const showDemoControls = state.useMockData
  let sourceLabel = "Mock fallback"
  let sourceDetail = "Using local preview"

  if (state.useMockData) {
    sourceLabel = "Mock sandbox"
    sourceDetail = "Scenario data"
  } else if (dataSource === "backend") {
    sourceLabel = "Backend live"
    sourceDetail = backendGeneratedAt
      ? `Synced ${new Date(backendGeneratedAt).toLocaleDateString()}`
      : "Synced"
  } else if (backendStatus === "loading") {
    sourceLabel = "Backend loading"
  } else if (backendStatus === "error") {
    sourceDetail = "Backend unavailable"
  }

  const handleMockDataToggle = () => {
    dispatch({ type: "setUseMockData", useMockData: !state.useMockData })
  }

  return (
    <div className="flex flex-wrap items-center justify-end gap-1.5">
      {showDemoControls ? (
        <div className="bg-muted text-muted-foreground flex rounded-md p-0.5 text-[11px] font-medium shadow-inner">
          <button
            type="button"
            className={cn(
              "btn-spring focus-visible:ring-ring inline-flex h-6 items-center gap-1 rounded px-2 transition focus-visible:ring-2 focus-visible:outline-none",
              state.role === "owner"
                ? "bg-background text-foreground ring-border/70 shadow-sm ring-1"
                : "hover:text-foreground"
            )}
            onClick={() => dispatch({ type: "setRole", role: "owner" })}
          >
            <UserRound className="size-3" />
            Owner
          </button>
          <button
            type="button"
            className={cn(
              "btn-spring focus-visible:ring-ring inline-flex h-6 items-center gap-1 rounded px-2 transition focus-visible:ring-2 focus-visible:outline-none",
              state.role === "admin"
                ? "bg-background text-foreground ring-border/70 shadow-sm ring-1"
                : "hover:text-foreground"
            )}
            onClick={() => dispatch({ type: "setRole", role: "admin" })}
          >
            <BriefcaseBusiness className="size-3" />
            Admin
          </button>
        </div>
      ) : (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="border-border bg-muted/60 text-muted-foreground inline-flex h-7 items-center gap-1 rounded-md border px-2 text-[11px] font-medium">
              {effectiveRole === "admin" ? (
                <BriefcaseBusiness className="size-3" />
              ) : (
                <UserRound className="size-3" />
              )}
              {ROLE_LABELS[effectiveRole]}
            </span>
          </TooltipTrigger>
          <TooltipContent>Your role is determined by your {APP_NAME} identity</TooltipContent>
        </Tooltip>
      )}
      {/* The mock-data switch is a demo affordance — only tenants that opted
          into the demo dataset get it; everyone else is live-data only (#2768). */}
      {SEGMENT_DEMO_DATA ? (
        <label
          className={cn(
            // Neutral surface in both states — tinting the whole pill orange made
            // the (orange) switch track melt into it. The sandbox signal is the
            // warning border + track + the "Scenario data" dot.
            "border-input bg-background/90 text-foreground hover:bg-accent/50 inline-flex h-7 cursor-pointer items-center gap-1.5 rounded-md border px-2 text-[11px] font-medium shadow-sm transition",
            state.useMockData && "border-warning/50"
          )}
        >
          {backendError ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Switch
                  aria-label="Use mock data"
                  checked={state.useMockData}
                  className="data-[state=checked]:bg-warning h-4 w-7 [&>span]:size-3 data-[state=checked]:[&>span]:translate-x-3"
                  onCheckedChange={handleMockDataToggle}
                />
              </TooltipTrigger>
              <TooltipContent>{backendError}</TooltipContent>
            </Tooltip>
          ) : (
            <Switch
              aria-label="Use mock data"
              checked={state.useMockData}
              className="data-[state=checked]:bg-warning h-4 w-7 [&>span]:size-3 data-[state=checked]:[&>span]:translate-x-3"
              onCheckedChange={handleMockDataToggle}
            />
          )}
          {sourceLabel}
        </label>
      ) : null}
      <span className="text-muted-foreground hidden items-center gap-1 text-[11px] md:inline-flex">
        {!state.useMockData && backendStatus === "error" ? (
          <AlertTriangle className="text-warning size-3" />
        ) : (
          <span
            className={cn(
              "size-1.5 rounded-full",
              dataSource === "backend" && "bg-success-ink",
              dataSource === "mock" && "bg-warning-ink",
              dataSource === "mock-fallback" && "bg-primary-ink border-foreground border"
            )}
          />
        )}
        {sourceDetail}
      </span>
      {showDemoControls ? (
        <span className="border-input bg-background text-foreground inline-flex h-7 items-center gap-1.5 rounded-md border px-2 text-[11px] shadow-sm">
          <span className="text-muted-foreground">Owner</span>
          <Select
            value={state.persona}
            onValueChange={(owner) => dispatch({ type: "setOwner", owner })}
          >
            <SelectTrigger
              aria-label="Owner"
              className="focus:ring-ring h-auto w-fit gap-1 border-0 bg-transparent p-0 text-[11px] font-medium shadow-none"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {owners.map((owner) => (
                <SelectItem key={owner} value={owner}>
                  {owner} ({allSegments.filter((segment) => segment.owner === owner).length})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </span>
      ) : null}
    </div>
  )
}
